import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
import { CheckoutType } from "../utils/sentry.utils";
import { useClarityTag } from "~features/tracking/analytics/integrations";

/**
 * Sets `checkoutType` as a persistent Sentry scope tag for the duration of
 * the checkout flow component's lifetime.
 *
 * This ensures that any exception captured by Sentry while the component is
 * mounted — including errors from child components, third-party libraries, and
 * unhandled promise rejections — is automatically tagged with the checkout type
 * so it can be filtered and monitored separately in the Sentry dashboard.
 *
 * The tag is removed when the component unmounts to prevent it from leaking
 * onto unrelated errors from other pages.
 *
 * @example
 * // In the top-level layout component of a checkout flow:
 * const PageLayout = () => {
 *   useCheckoutSentryScope("standard");
 *   // ...
 * };
 */
export function useCheckoutTrackingScope(checkoutType: CheckoutType): void {
  const setClarityTag = useClarityTag();

  useEffect(() => {
    Sentry.configureScope((scope) => {
      scope.setTag("checkoutType", checkoutType);
    });
    setClarityTag("checkoutType", checkoutType);

    return () => {
      Sentry.configureScope((scope) => {
        scope.setTag("checkoutType", undefined);
      });
    };
  }, [checkoutType, setClarityTag]);
}
